import { Icon } from '@iconify/react';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useState } from 'react';
import { toast } from 'react-toastify';
import { logError } from '../../../utils/logger';
import { validateProps } from '../../../utils/propValidator';
import { ConfirmationModal } from '../../shared/components';
import { capitalizeString, formatNumber } from '../../shared/utils/misc';
import { deleteWebsiteProject } from '../website.service';

const propRules = {
  project: {
    type: 'object',
    required: true,
    shape: {
      id: { type: 'number', required: true },
      title: { type: 'string', required: true },
      projectType: { type: 'string', required: true },
      location: { type: 'string', required: true },
      completionYear: { type: ['string', 'number'] },
      builderName: { type: 'string', required: true },
      builderContact: { type: 'string' },
      area: { type: 'string', required: true },
      status: { type: 'string', required: true },
      inProgressStatus: { type: 'string', required: true },
      description: { type: 'string', required: true },
      tags: { type: 'array', required: true },
      photos: { type: 'array', required: true },
    },
  },
  index: { type: 'number', required: true },
  onEdit: { type: 'function', required: true },
};

export default function WebsiteProjectDisplayCard(props) {
  validateProps(props as Record<string, unknown>, propRules, 'WebsiteProjectDisplayCard');
  const { project, index } = props;
  const {
    id,
    title,
    projectType,
    location,
    completionYear,
    builderName,
    builderContact,
    area,
    status,
    inProgressStatus,
    description,
    tags,
    photos,
  } = project || {};

  const statusStr =
    status?.toLowerCase() === 'completed' ? `Completed, Year ${completionYear}` : inProgressStatus;

  const [openDeleteConfirm, setOpenDeleteConfirm] = useState(false);
  const [deleting, setDeleting] = useState(false);

  const handleDelete = async () => {
    setDeleting(true);
    try {
      await deleteWebsiteProject(id);
      props.onDelete();
    } catch (err) {
      toast.error(
        logError({
          message: 'Failed to delete website project',
          error: err,
          code: 'WEBSITE_ACTION_FAILED',
        })
      );
    } finally {
      setDeleting(false);
      setOpenDeleteConfirm(false);
    }
  };

  return (
    <>
      <div className='column'>
        <div className='row row--between'>
          <Typography variant='subtitle2' component='h3'>
            Section {index + 1}
          </Typography>
          <div className='row'>
            <button type='button' className='btn text-btn text-btn--small' onClick={props.onEdit}>
              <Icon icon='line-md:edit-twotone' width={16} />
              Edit
            </button>
            <button
              type='button'
              className='btn text-btn text-btn--small'
              onClick={() => setOpenDeleteConfirm(true)}
            >
              <Icon icon='material-symbols:delete-outline' width={16} />
              Delete
            </button>
          </div>
        </div>
        <Stack
          gap={3}
          sx={{ padding: 2, border: '1px solid', borderColor: 'grey.300', borderRadius: 2 }}
        >
          <div className='row row--between'>
            <Typography variant='body1' fontWeight={500} fontSize='1rem' component='h4'>
              {title}
            </Typography>
            <Typography variant='body1' fontWeight={500} component='h4'>
              {capitalizeString(projectType)} Project
            </Typography>
          </div>
          <div className='row row--between'>
            <div className='row'>
              <span className='label'>Location:</span> <span className='value'>{location}</span>
            </div>
            <div className='row'>
              <span className='label'>Status:</span>
              <span className='value'>{statusStr}</span>
            </div>
          </div>
          <div className='row row--between'>
            <div className='row'>
              <span className='label'>Builder:</span>{' '}
              <span className='value'>
                {builderName}
                {builderContact ? ` (${builderContact})` : ''}
              </span>
            </div>
            <div className='row'>
              <span className='label'>Total area of project:</span>
              <span className='value'>{formatNumber(area)} sq.ft</span>
            </div>
          </div>

          <p className='text-primary'>{description}</p>

          <div className='row'>
            {tags.map((tag, index) => (
              <span key={index} className='text--secondary'>
                {tag}
              </span>
            ))}
          </div>

          <div className='grid grid--5-cols'>
            {photos.map((photo) => (
              <img src={photo.url} alt='' key={photo.id} className='aspect-16-9' />
            ))}
          </div>
        </Stack>
      </div>
      <ConfirmationModal
        open={openDeleteConfirm}
        onClose={() => setOpenDeleteConfirm(false)}
        title='Delete Project'
        message='Are you sure you want to delete this project?'
        onConfirm={handleDelete}
        confirmText='Delete'
        icon='mdi:delete-outline'
        loading={deleting}
      />
    </>
  );
}
